// Toast notifications for mutation feedback.
//
// Two ways in, one toaster:
//   notify({ msg, tone })      — imperative, works anywhere (event handlers,
//                                 plain async functions, components rendered
//                                 outside any provider). Preferred.
//   useToast()({ msg, tone })  — the hook form; identical, kept for the
//                                 pages that already use it.
//
// House rule (23 Sep 2026): every successful write on an /ops page ends in
// a toast — bottom-right, auto-dismissing — so the person knows it landed.
// The toaster mounts itself lazily in its own React root under `.helix`
// (theme tokens apply) the first time a toast is pushed, so no page has to
// wrap anything. <HxToastProvider> is kept as a no-op boundary for the
// pages that render it.

import { useEffect, useState, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { helixRoot } from "./hooks";
import { Button } from "../ui/button";

type Tone = "ok" | "neg" | "warn";
interface ToastAction { label: string; onClick: () => void }
interface ToastItem {
  id: number;
  msg: string;
  tone: Tone;
  action?: ToastAction;
  sticky?: boolean;
}
/** `sticky` keeps the toast up until its action is taken — for a message the
 *  person has to act on (a new build is out), not one that confirms a write.
 *  A sticky toast sits top-right, under the top bar, where it is seen; write
 *  confirmations stay bottom-right. */
export interface ToastInput { msg: string; tone?: Tone; action?: ToastAction; sticky?: boolean }

const TOAST_MS = 3600;
let items: ToastItem[] = [];
let nextId = 1;
const listeners = new Set<(items: ToastItem[]) => void>();
let host: HTMLDivElement | null = null;

function emit() {
  for (const l of listeners) l(items);
}

// The host lives under the CURRENT `.helix` element so the theme tokens
// apply — but every page renders its own Shell, so that element is thrown
// away on navigation. A host mounted once and forgotten was left detached
// after the first route change and every later toast rendered into nothing
// (23 Sep 2026). So: keep one React root, and on every toast re-parent the
// host under whatever `.helix` is live now. Moving a DOM node does not
// disturb the React root inside it.
function ensureMounted() {
  if (!host) {
    host = document.createElement("div");
    host.className = "hx-toaster-host";
    createRoot(host).render(<Toaster />);
  }
  const root = helixRoot();
  if (host.parentElement !== root) root.appendChild(host);
}

export function notify(t: ToastInput): void {
  ensureMounted();
  const id = nextId++;
  items = [...items, { id, msg: t.msg, tone: t.tone ?? "ok", action: t.action, sticky: t.sticky }];
  emit();
  if (t.sticky) return;
  setTimeout(() => {
    items = items.filter((i) => i.id !== id);
    emit();
  }, TOAST_MS);
}

function Toaster() {
  const [list, setList] = useState<ToastItem[]>(items);
  useEffect(() => {
    listeners.add(setList);
    return () => { listeners.delete(setList); };
  }, []);
  if (list.length === 0) return null;
  const top = list.filter((i) => i.sticky);
  const bottom = list.filter((i) => !i.sticky);
  return (
    <>
      {top.length > 0 && <ToastStack list={top} className="hx-toaster top" />}
      {bottom.length > 0 && <ToastStack list={bottom} className="hx-toaster" />}
    </>
  );
}

function ToastStack({ list, className }: { list: ToastItem[]; className: string }) {
  return (
    <div className={className} role="status" aria-live="polite">
      {list.map((i) => (
        <div key={i.id} className={`hx-toast ${i.tone}`}>
          {i.msg}
          {i.action && (
            <Button type="button" size="sm" variant="outline" className="ml-auto shrink-0" onClick={i.action.onClick}>
              {i.action.label}
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}

export function useToast(): (t: ToastInput) => void {
  return notify;
}

export function HxToastProvider({ children }: { children: ReactNode }) {
  return <>{children}</>;
}
